import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Book,
  Lightbulb,
  MessageSquare,
  Mail,
  Video,
  Rocket,
  X,
  ExternalLink,
  FileText,
  Settings,
  Calendar,
  Users,
  BarChart2
} from 'lucide-react';

interface HelpMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpMenu: React.FC<HelpMenuProps> = ({ isOpen, onClose }) => {
  const sections = [
    {
      title: 'Quick Start',
      items: [
        {
          icon: Rocket,
          title: 'Getting Started Guide',
          description: 'Learn the basics of using the platform',
          link: '/learning-hub'
        },
        {
          icon: Video,
          title: 'Video Tutorials',
          description: 'Watch step-by-step guides',
          link: '/learning-hub'
        }
      ]
    },
    {
      title: 'Feature Guides',
      items: [
        {
          icon: Calendar,
          title: 'Content Calendar',
          description: 'Learn to schedule and manage content',
          link: '/calendar'
        },
        {
          icon: Users,
          title: 'Contact Management',
          description: 'Organize your network effectively',
          link: '/contacts'
        },
        {
          icon: BarChart2,
          title: 'Analytics',
          description: 'Understanding your performance metrics',
          link: '/performance'
        }
      ]
    },
    {
      title: 'Support',
      items: [
        {
          icon: MessageSquare,
          title: 'Chat with AI Assistant',
          description: 'Get instant help with content creation',
          link: '/creator-assistant'
        },
        {
          icon: Mail,
          title: 'Contact Support',
          description: 'Email our support team',
          link: 'mailto:support@serge.com'
        }
      ]
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="fixed right-0 sm:right-4 top-16 sm:top-20 w-full sm:w-[480px] bg-white dark:bg-dark-800 rounded-xl shadow-xl z-50 overflow-hidden max-w-[100vw] sm:max-w-[480px]"
          >
            {/* Header */}
            <div className="p-4 sm:p-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Book className="h-6 w-6" />
                  <h2 className="text-xl font-semibold">Help & Resources</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-indigo-100">Find guides, tutorials, and support resources</p>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="space-y-6">
                {sections.map((section, index) => (
                  <div key={index}>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      {section.title}
                    </h3>
                    <div className="space-y-2">
                      {section.items.map((item, itemIndex) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={itemIndex}
                            to={item.link}
                            onClick={onClose}
                            className="flex items-start p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors group"
                          >
                            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                              <Icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div className="ml-4 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {item.title}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {item.description}
                              </p>
                            </div>
                            <ExternalLink className="h-5 w-5 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Documentation Link */}
              <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    <div>
                      <h4 className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                        Full Documentation
                      </h4>
                      <p className="text-xs text-indigo-700 dark:text-indigo-300">
                        Detailed guides and API references
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/docs"
                    onClick={onClose}
                    className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    View Docs
                  </Link>
                </div>
              </div>

              {/* Quick Tips */}
              <div className="mt-6 border-t border-gray-100 dark:border-dark-700 pt-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Quick Tips</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    • Use keyboard shortcut <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-dark-700 rounded text-xs">?</kbd> to open this menu
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    • Customize your dashboard layout in <Link to="/settings" className="text-indigo-600 dark:text-indigo-400 hover:underline">Settings</Link>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    • Connect your social accounts to enable all features
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};